import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Download, RefreshCw } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const cardCls =
  "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const inputCls =
  "w-full p-2.5 rounded-lg bg-black/40 border border-white/20 text-white placeholder-white/30 " +
  "outline-none focus:border-[#00FF99]/60 text-sm";

const secondaryBtn =
  "inline-flex items-center gap-2 bg-white/5 border border-white/15 text-white/90 px-3 py-2 " +
  "rounded-lg hover:bg-white/10 transition text-sm";

const disabledBtn =
  "inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white/40 px-3 py-2 " +
  "rounded-lg cursor-not-allowed text-sm";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function describeResource(row) {
  if (row.resource_name) return row.resource_name;
  if (row.resource_type && row.resource_id) return `${row.resource_type}:${row.resource_id.slice(0, 8)}…`;
  return row.resource_type || "—";
}

function describeDetails(d) {
  if (!d || typeof d !== "object") return "—";
  const parts = [];
  for (const [k, v] of Object.entries(d)) {
    if (v === null || v === undefined) continue;
    parts.push(`${k}=${typeof v === "string" ? v : JSON.stringify(v)}`);
  }
  return parts.length ? parts.join("  ·  ") : "—";
}

export default function SuperadminAuditLog() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [search, setSearch]   = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/audit/superadmin?limit=100`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || `Failed to load audit log (${res.status})`);
        setRows([]);
        return;
      }
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError("Network error.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r.actor_email,
        r.action,
        r.resource_type,
        r.resource_name,
        JSON.stringify(r.details || {}),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white">Audit Log</h2>
        <p className="text-sm text-white/50 mt-1">
          Read-only history of administrative actions across the platform.
        </p>
      </div>

      <div className={`${cardCls} p-4 flex flex-wrap items-center gap-3`}>
        <div className="relative flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            className={inputCls + " pl-9"}
            placeholder="Filter by actor, action, resource or details…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button onClick={load} className={secondaryBtn} title="Refresh">
          <RefreshCw size={14} />
        </button>
        <button className={disabledBtn} disabled>
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className={`${cardCls} overflow-hidden`}>
        {loading ? (
          <div className="p-8 text-center text-white/50">Loading audit entries…</div>
        ) : error ? (
          <div className="p-8 text-center text-red-400">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-white/50">
            {rows.length === 0 ? "No audit entries yet." : "No entries match your filter."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-white/60 text-xs uppercase">
                <tr>
                  <th className="text-left px-5 py-3">Date</th>
                  <th className="text-left px-5 py-3">Actor</th>
                  <th className="text-left px-5 py-3">Action</th>
                  <th className="text-left px-5 py-3">Resource</th>
                  <th className="text-left px-5 py-3">Details</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-white/5">
                    <td className="px-5 py-3 text-white/60 font-mono text-xs whitespace-nowrap">
                      {formatDate(r.created_at)}
                    </td>
                    <td className="px-5 py-3 text-white/80">{r.actor_email || "—"}</td>
                    <td className="px-5 py-3 text-[#00FF99] font-mono text-xs">{r.action}</td>
                    <td className="px-5 py-3 text-white/70">
                      <span className="text-white/40 text-xs">{r.resource_type || "—"}</span>
                      {r.resource_name && (
                        <>
                          <span className="text-white/20 mx-1">·</span>
                          <span className="text-white/80">{describeResource(r)}</span>
                        </>
                      )}
                    </td>
                    <td className="px-5 py-3 text-white/60 font-mono text-xs">
                      {describeDetails(r.details)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && rows.length > 0 && (
        <p className="text-xs text-white/40 text-center pt-2">
          Showing latest {rows.length} entries (max 100)
        </p>
      )}
    </div>
  );
}
